/**
 * Menu Activity tab: page views and report downloads per user (activity_log)
 */

import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Center,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Input,
    SimpleGrid,
    Spinner,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useToast,
    useToken,
    VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { format, parseISO } from "date-fns";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ACTIVITY_LOGS_API_CONFIG } from "./apiConfig";
import { ActivityRange } from "./types";
import { useActivityStats, useUserMenus } from "./useActivityStats";

const { ACTIVE_USERS_LIMIT, DEFAULT_RANGE_DAYS } = ACTIVITY_LOGS_API_CONFIG;
const API_DATE = "YYYY-MM-DD";

const defaultRange = (): ActivityRange => ({
    start_date: dayjs().subtract(DEFAULT_RANGE_DAYS - 1, "day").format(API_DATE),
    end_date: dayjs().format(API_DATE),
});

const displayDate = (isoDate: string) => format(parseISO(isoDate), "dd/MM/yyyy");
const displayDateTime = (isoDateTime: string) => format(new Date(isoDateTime), "dd/MM/yyyy HH:mm");
const num = (n: number) => n.toLocaleString("id-ID");

interface Column<T> {
    header: string;
    render: (row: T) => React.ReactNode;
    isNumeric?: boolean;
}

interface StatTableProps<T> {
    title: string;
    rows: T[];
    columns: Column<T>[];
    rowKey: (row: T) => string;
    onRowClick?: (row: T) => void;
    isSelected?: (row: T) => boolean;
    loading?: boolean;
}

function StatTable<T>({ title, rows, columns, rowKey, onRowClick, isSelected, loading }: StatTableProps<T>) {
    const cardBg = useColorModeValue("white", "gray.800");
    const hoverBg = useColorModeValue("gray.50", "gray.700");
    const selectedBg = useColorModeValue("gray.100", "gray.600");

    return (
        <Box bg={cardBg} borderRadius="md" boxShadow="sm" p={4}>
            <Heading size="sm" mb={3}>
                {title}
            </Heading>
            {loading ? (
                <Center py={8}>
                    <Spinner />
                </Center>
            ) : rows.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                    Belum ada data pada rentang tanggal ini.
                </Text>
            ) : (
                <TableContainer maxH="360px" overflowY="auto">
                    <Table size="sm">
                        <Thead position="sticky" top={0} bg={cardBg}>
                            <Tr>
                                {columns.map((c) => (
                                    <Th key={c.header} isNumeric={c.isNumeric}>
                                        {c.header}
                                    </Th>
                                ))}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.map((row) => (
                                <Tr
                                    key={rowKey(row)}
                                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                                    cursor={onRowClick ? "pointer" : undefined}
                                    bg={isSelected?.(row) ? selectedBg : undefined}
                                    fontWeight={isSelected?.(row) ? "semibold" : undefined}
                                    _hover={onRowClick ? { bg: hoverBg } : undefined}
                                >
                                    {columns.map((c) => (
                                        <Td key={c.header} isNumeric={c.isNumeric}>
                                            {c.render(row)}
                                        </Td>
                                    ))}
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

const ActivityLogsDashboard: React.FC = () => {
    const toast = useToast();
    const [draft, setDraft] = useState<ActivityRange>(defaultRange);
    const [range, setRange] = useState<ActivityRange>(defaultRange);
    const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

    const { data, loading, error } = useActivityStats(range);
    const userMenus = useUserMenus(range, selectedEmail);

    const [lineColor, gridColor] = useToken("colors", ["customGreen.500", "gray.200"]);
    const cardBg = useColorModeValue("white", "gray.800");

    useEffect(() => {
        const message = error ?? userMenus.error;
        if (message) {
            toast({ title: "Gagal memuat aktivitas", description: message, status: "error", duration: 3000, isClosable: true });
        }
    }, [error, userMenus.error, toast]);

    const applyRange = () => {
        if (draft.start_date > draft.end_date) {
            toast({ title: "Tanggal mulai harus sebelum tanggal akhir", status: "warning", duration: 3000, isClosable: true });
            return;
        }
        setSelectedEmail(null);
        setRange({ ...draft });
    };

    const daily = data?.daily ?? [];
    const totalPageViews = daily.reduce((sum, d) => sum + d.page_views, 0);
    const totalDownloads = daily.reduce((sum, d) => sum + d.downloads, 0);
    const activeUsers = data?.topUsers.length ?? 0;
    const chartData = daily.map((d) => ({ ...d, label: format(parseISO(d.day), "dd/MM") }));

    return (
        <VStack align="stretch" spacing={6}>
            <HStack align="flex-end" spacing={4} flexWrap="wrap">
                <FormControl w="auto">
                    <FormLabel fontSize="sm">Dari</FormLabel>
                    <Input
                        type="date"
                        size="sm"
                        value={draft.start_date}
                        max={draft.end_date}
                        onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                    />
                </FormControl>
                <FormControl w="auto">
                    <FormLabel fontSize="sm">Sampai</FormLabel>
                    <Input
                        type="date"
                        size="sm"
                        value={draft.end_date}
                        min={draft.start_date}
                        onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                    />
                </FormControl>
                <Button size="sm" onClick={applyRange} isLoading={loading}>
                    Terapkan
                </Button>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Stat bg={cardBg} p={4} borderRadius="md" boxShadow="sm">
                    <StatLabel>Kunjungan menu</StatLabel>
                    <StatNumber>{loading ? "–" : num(totalPageViews)}</StatNumber>
                    <StatHelpText>
                        {displayDate(range.start_date)} – {displayDate(range.end_date)}
                    </StatHelpText>
                </Stat>
                <Stat bg={cardBg} p={4} borderRadius="md" boxShadow="sm">
                    <StatLabel>Laporan diunduh</StatLabel>
                    <StatNumber>{loading ? "–" : num(totalDownloads)}</StatNumber>
                    <StatHelpText>Semua jenis laporan</StatHelpText>
                </Stat>
                <Stat bg={cardBg} p={4} borderRadius="md" boxShadow="sm">
                    <StatLabel>User aktif</StatLabel>
                    <StatNumber>
                        {loading ? "–" : activeUsers >= ACTIVE_USERS_LIMIT ? `${ACTIVE_USERS_LIMIT}+` : num(activeUsers)}
                    </StatNumber>
                    <StatHelpText>Minimal satu aktivitas</StatHelpText>
                </Stat>
            </SimpleGrid>

            <Box bg={cardBg} borderRadius="md" boxShadow="sm" p={4}>
                <Heading size="sm" mb={3}>
                    Kunjungan menu per hari
                </Heading>
                {loading ? (
                    <Center h="260px">
                        <Spinner />
                    </Center>
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid vertical={false} stroke={gridColor} />
                            <XAxis dataKey="label" fontSize={12} tickLine={false} />
                            <YAxis allowDecimals={false} fontSize={12} tickLine={false} axisLine={false} width={40} />
                            <Tooltip
                                labelFormatter={(_, payload) =>
                                    payload?.[0] ? displayDate(payload[0].payload.day) : ""
                                }
                                formatter={(value: number) => [`${num(value)} kunjungan`, "Kunjungan"]}
                            />
                            <Line
                                type="monotone"
                                dataKey="page_views"
                                stroke={lineColor}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Box>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                <StatTable
                    title="Menu paling sering dikunjungi"
                    loading={loading}
                    rows={data?.topMenus ?? []}
                    rowKey={(r) => r.menu_key ?? "-"}
                    columns={[
                        { header: "Menu", render: (r) => r.menu_name ?? r.menu_key },
                        { header: "Kunjungan", render: (r) => num(r.visits), isNumeric: true },
                        { header: "User", render: (r) => num(r.unique_users), isNumeric: true },
                    ]}
                />
                <StatTable
                    title="Laporan paling sering diunduh"
                    loading={loading}
                    rows={data?.topDownloads ?? []}
                    rowKey={(r) => r.resource ?? "-"}
                    columns={[
                        { header: "Laporan", render: (r) => r.resource ?? "-" },
                        { header: "Unduhan", render: (r) => num(r.downloads), isNumeric: true },
                        { header: "User", render: (r) => num(r.unique_users), isNumeric: true },
                    ]}
                />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                <StatTable
                    title="User paling aktif (klik untuk melihat menu favoritnya)"
                    loading={loading}
                    rows={data?.topUsers ?? []}
                    rowKey={(r) => r.email}
                    onRowClick={(r) => setSelectedEmail(r.email === selectedEmail ? null : r.email)}
                    isSelected={(r) => r.email === selectedEmail}
                    columns={[
                        { header: "User", render: (r) => r.email },
                        { header: "Hari aktif", render: (r) => num(r.active_days), isNumeric: true },
                        { header: "Kunjungan", render: (r) => num(r.page_views), isNumeric: true },
                        { header: "Unduhan", render: (r) => num(r.downloads), isNumeric: true },
                        { header: "Terakhir aktif", render: (r) => displayDateTime(r.last_seen) },
                    ]}
                />
                {selectedEmail ? (
                    <StatTable
                        title={`Menu favorit ${selectedEmail}`}
                        loading={userMenus.loading}
                        rows={userMenus.data}
                        rowKey={(r) => r.menu_key ?? "-"}
                        columns={[
                            { header: "Menu", render: (r) => r.menu_name ?? r.menu_key },
                            { header: "Kunjungan", render: (r) => num(r.visits), isNumeric: true },
                        ]}
                    />
                ) : (
                    <Center bg={cardBg} borderRadius="md" boxShadow="sm" p={4} minH="120px">
                        <Text fontSize="sm" color="gray.500">
                            Pilih user di tabel sebelah untuk melihat menu yang paling sering dibukanya.
                        </Text>
                    </Center>
                )}
            </SimpleGrid>
        </VStack>
    );
};

export default ActivityLogsDashboard;
